import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { agents, sessions } from '../api';
import { Bot, Phone, PhoneOff, Settings, MoreVertical, Mic, Play, Pause, Copy, Trash2, Edit3, PhoneCall, Code, Archive, ArchiveRestore, AlertTriangle, Search, X, LayoutGrid, List, MessageSquare, GitBranch, Circle, Sparkles, MousePointerClick } from 'lucide-react';
import VoiceWaveform from './VoiceWaveform';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './DropdownMenu';
import TestAgentInterface from './TestAgentInterface';
import EmbedModal from './EmbedModal';
import ChatEmbedModal from './ChatEmbedModal';
import VoiceWidgetEmbedModal from './VoiceWidgetEmbedModal';
import UnifiedWidgetEmbedModal from './UnifiedWidgetEmbedModal';
import ComboWidgetV2EmbedModal from './ComboWidgetV2EmbedModal';
import AvatarPanelEmbedModal from './AvatarPanelEmbedModal';
import CobrowseEmbedModal from './CobrowseEmbedModal';
import LogoVoiceWidgetEmbedModal from './LogoVoiceWidgetEmbedModal';
import EmbedDynamicVariables from './EmbedDynamicVariables';
import EmbedDynamicVariablesLiveAvatar from './EmbedDynamicVariablesLiveAvatar';
import AgentFormModal from './AgentFormModal';
import { serializeTransferRules } from './TransferRulesEditor';
import Toast from './Toast';
import LoadingSpinner from './LoadingSpinner';

const createDefaultFormState = () => ({
  name: '',
  system_prompt: '',
  chat_system_prompt: '',
  agent_type: 'prompt',
  global_prompt: '',
  conversation_flow: null,
  required_variables: [],
  llm_model: 'gpt-4o-mini',
  extraction_llm_model: 'gpt-4.1',
  temperature: 0.7,
  locale: 'en-US',
  voice_provider: 'elevenlabs',
  elevenlabs_voice_id: '21m00Tcm4TlvDq8ikWAM',
  elevenlabs_model: 'eleven_turbo_v2_5',
  cartesia_voice_id: '',
  sonic_voice_id: '',
  sonic_model: 'sonic-3',
  cartesia_language: '',
  voice_temperature: 0.7,
  voice_stability: 0.6,
  voice_volume: 1,
  elevenlabs_speed: 1.0,
  who_speaks_first: 'user',
  greeting_message: '',
  greeting_non_interruptible: false,
  outbound_greeting_enabled: false,
  outbound_greeting_message: '',
  outbound_who_speaks_first_enabled: false,
  outbound_who_speaks_first: 'ai',
  outbound_greeting_non_interruptible: false,
  disable_interruption: false,
  is_active: true,
  pii_policy: 'off',
  custom_pii_types: '',
  send_redacted_to_webhook: false,
  inbound_webhook_url: '',
  outbound_webhook_url: '',
  chat_outbound_webhook_url: '',
  sonic_speed: 1.0,
  sonic_volume: 1.0,
  sonic_emotion: 'neutral',
  fishaudio_voice_id: '',
  fishaudio_model: 's2.1-pro',
  fishaudio_latency_mode: 'balanced',
  fishaudio_speed: 1.0,
  fishaudio_volume: 0,
  fishaudio_temperature: 0.7,
  fishaudio_top_p: 0.7,
  fishaudio_normalize: true,
  fishaudio_normalize_loudness: true,
  fishaudio_chunk_length: 100,
  fishaudio_min_chunk_length: 50,
  fishaudio_max_new_tokens: 1024,
  fishaudio_condition_on_previous_chunks: true,
  fishaudio_early_stop_threshold: 1.0,
  fishaudio_expressive_enabled: false,
  fishaudio_expressive_pace: 'normal',
  fishaudio_expressive_disfluencies: true,
  fishaudio_expressive_nonverbal: true,
  fishaudio_expressive_instructions: '',
  min_silence_duration_enabled: false,
  min_silence_duration: 0.55,
  transfer_enabled: false,
  transfer_type: 'warm',
  transfer_target_number: '',
  transfer_rules: null,
  agent_transfer_enabled: false,
  agent_transfer_agents: null,
  stt_provider: 'deepgram',
  stt_model: 'nova3_mono_en',
  stt_language: 'en-US',
  gladia_languages: null,
  gladia_translation_enabled: false,
  gladia_translation_target_languages: null,
  gladia_endpointing: null,
  gladia_speech_threshold: null,
  soniox_model: null,
  soniox_language_hints: null,
  soniox_language_hints_strict: false,
  soniox_enable_language_identification: true,
  soniox_translation_enabled: false,
  soniox_translation_source_languages: null,
  soniox_translation_target_languages: null,
  soniox_translation_terms: null,
  soniox_context: null,
  disable_call_recording: false,
  backchanneling_enabled: false,
  background_audio_enabled: false,
  background_audio_file_url: null,
  background_audio_volume: 0.3,
  reminders_enabled: false,
  reminders_timer_seconds: 30,
  reminders_words: null,
  silence_hangup_enabled: false,
  silence_hangup_seconds: 30,
  silence_hangup_message: '',
  max_call_duration_enabled: false,
  max_call_duration_seconds: 600,
  max_call_duration_message: '',
  backchanneling_words: null,
  liveavatar_enabled: false,
  liveavatar_avatar_id: '',
  chat_reminder_enabled: false,
  chat_reminder_seconds: 30,
  chat_reminder_type: 'static',
  chat_reminder_message: '',
  chat_auto_close_enabled: false,
  chat_auto_close_seconds: 30,
  chat_auto_close_message: '',
  agent_end_call_enabled: false,
  agent_end_call_speak_before: false,
  agent_end_call_message_type: 'prompt',
  agent_end_call_message: '',
  agent_end_call_goodbye_phrases: null,
  voicemail_drop_enabled: false,
  voicemail_message: '',
  dtmf_enabled: false,
  dtmf_ivr_detection: false,
  ivr_qa_mode: false,
  ivr_qa_max_turns: 25,
  ivr_qa_max_duration_s: 600,
  ivr_qa_goal: 'reach_operator',
  ivr_qa_prompt_override: '',
  dtln_enabled: false,
  dtln_strength: 0.5,
  ai_acoustics_enabled: false,
  ai_acoustics_enhancement_level: 0.8,
  ai_acoustics_model: 'quail_vf_l',
});

const mapAgentToFormState = (agent) => ({
  name: agent?.name || '',
  system_prompt: agent?.system_prompt || '',
  chat_system_prompt: agent?.chat_system_prompt || '',
  agent_type: agent?.agent_type || 'prompt',
  global_prompt: agent?.global_prompt || '',
  conversation_flow: agent?.conversation_flow || null,
  required_variables: agent?.required_variables || [],
  llm_model: agent?.llm_model || 'gpt-4o-mini',
  temperature: agent?.temperature ?? 0.7,
  locale: agent?.locale || 'en-US',
  voice_provider: agent?.voice_provider || 'elevenlabs',
  elevenlabs_voice_id: agent?.elevenlabs_voice_id || '21m00Tcm4TlvDq8ikWAM',
  // Legacy agents may still carry the retired eleven_monolingual_v1 -- surface them as Turbo v2.5
  elevenlabs_model: (agent?.elevenlabs_model && agent.elevenlabs_model !== 'eleven_monolingual_v1')
    ? agent.elevenlabs_model
    : (agent?.voice_provider === 'elevenlabs' ? 'eleven_turbo_v2_5' : null),
  cartesia_voice_id: agent?.cartesia_voice_id || '',
  sonic_voice_id: agent?.sonic_voice_id || '',
  sonic_model: agent?.sonic_model || 'sonic-3',
  cartesia_language: agent?.cartesia_language || '',
  voice_temperature: agent?.voice_temperature ?? 0.7,
  voice_stability: agent?.voice_stability ?? 0.6,
  voice_volume: agent?.voice_volume ?? 1,
  elevenlabs_speed: agent?.elevenlabs_speed ?? 1.0,
  who_speaks_first: agent?.who_speaks_first || 'user',
  greeting_message: agent?.greeting_message || '',
  greeting_non_interruptible: agent?.greeting_non_interruptible ?? false,
  outbound_greeting_enabled: agent?.outbound_greeting_enabled ?? false,
  outbound_greeting_message: agent?.outbound_greeting_message || '',
  outbound_who_speaks_first_enabled: agent?.outbound_who_speaks_first_enabled ?? false,
  outbound_who_speaks_first: agent?.outbound_who_speaks_first || 'ai',
  outbound_greeting_non_interruptible: agent?.outbound_greeting_non_interruptible ?? false,
  disable_interruption: agent?.disable_interruption ?? false,
  is_active: agent?.is_active ?? true,
  pii_policy: agent?.pii_policy || 'off',
  custom_pii_types: agent?.custom_pii_types || '',
  send_redacted_to_webhook: agent?.send_redacted_to_webhook ?? false,
  inbound_webhook_url: agent?.inbound_webhook_url || '',
  outbound_webhook_url: agent?.outbound_webhook_url || '',
  chat_outbound_webhook_url: agent?.chat_outbound_webhook_url || '',
  sonic_speed: agent?.sonic_speed ?? 1.0,
  sonic_volume: agent?.sonic_volume ?? 1.0,
  sonic_emotion: agent?.sonic_emotion || 'neutral',
  fishaudio_voice_id: agent?.fishaudio_voice_id || '',
  fishaudio_model: agent?.fishaudio_model || 's2.1-pro',
  fishaudio_latency_mode: agent?.fishaudio_latency_mode || 'balanced',
  fishaudio_speed: agent?.fishaudio_speed ?? 1.0,
  fishaudio_volume: agent?.fishaudio_volume ?? 0,
  fishaudio_temperature: agent?.fishaudio_temperature ?? 0.7,
  fishaudio_top_p: agent?.fishaudio_top_p ?? 0.7,
  fishaudio_normalize: agent?.fishaudio_normalize ?? true,
  fishaudio_normalize_loudness: agent?.fishaudio_normalize_loudness ?? true,
  fishaudio_chunk_length: agent?.fishaudio_chunk_length ?? 100,
  fishaudio_min_chunk_length: agent?.fishaudio_min_chunk_length ?? 50,
  fishaudio_max_new_tokens: agent?.fishaudio_max_new_tokens ?? 1024,
  fishaudio_condition_on_previous_chunks: agent?.fishaudio_condition_on_previous_chunks ?? true,
  fishaudio_early_stop_threshold: agent?.fishaudio_early_stop_threshold ?? 1.0,
  fishaudio_expressive_enabled: agent?.fishaudio_expressive_enabled ?? false,
  fishaudio_expressive_pace: agent?.fishaudio_expressive_pace || 'normal',
  fishaudio_expressive_disfluencies: agent?.fishaudio_expressive_disfluencies ?? true,
  fishaudio_expressive_nonverbal: agent?.fishaudio_expressive_nonverbal ?? true,
  fishaudio_expressive_instructions: agent?.fishaudio_expressive_instructions || '',
  min_silence_duration_enabled: agent?.min_silence_duration_enabled ?? false,
  min_silence_duration: agent?.min_silence_duration ?? 0.55,
  transfer_enabled: agent?.transfer_enabled ?? false,
  transfer_type: agent?.transfer_type || 'warm',
  transfer_target_number: agent?.transfer_target_number || '',
  transfer_rules: agent?.transfer_rules || null,
  agent_transfer_enabled: agent?.agent_transfer_enabled ?? false,
  agent_transfer_agents: agent?.agent_transfer_agents || null,
  extraction_llm_model: agent?.extraction_llm_model || 'gpt-4.1',
  stt_provider: agent?.stt_provider || 'deepgram',
  stt_model: agent?.stt_model || 'nova3_mono_en',
  // Enforce: stt_language MUST be 'multi' when stt_model is 'nova3_multi'
  stt_language: (agent?.stt_model || 'nova3_mono_en') === 'nova3_multi' ? 'multi' : (agent?.stt_language || 'en-US'),
  gladia_languages: agent?.gladia_languages || null,
  gladia_translation_enabled: agent?.gladia_translation_enabled ?? false,
  gladia_translation_target_languages: agent?.gladia_translation_target_languages || null,
  gladia_endpointing: agent?.gladia_endpointing ?? null,
  gladia_speech_threshold: agent?.gladia_speech_threshold ?? null,
  soniox_model: agent?.soniox_model || null,
  soniox_language_hints: agent?.soniox_language_hints || null,
  soniox_language_hints_strict: agent?.soniox_language_hints_strict ?? false,
  soniox_enable_language_identification: agent?.soniox_enable_language_identification ?? true,
  soniox_translation_enabled: agent?.soniox_translation_enabled ?? false,
  soniox_translation_source_languages: agent?.soniox_translation_source_languages || null,
  soniox_translation_target_languages: agent?.soniox_translation_target_languages || null,
  soniox_translation_terms: agent?.soniox_translation_terms || null,
  soniox_context: agent?.soniox_context || null,
  disable_call_recording: agent?.disable_call_recording ?? false,
  backchanneling_enabled: agent?.backchanneling_enabled ?? false,
  backchanneling_words: (agent?.backchanneling_words && agent.backchanneling_words.trim && agent.backchanneling_words.trim() !== '') ? agent.backchanneling_words : null,
  background_audio_enabled: agent?.background_audio_enabled ?? false,
  background_audio_file_url: agent?.background_audio_file_url ?? null,
  background_audio_volume: agent?.background_audio_volume ?? 0.3,
  reminders_enabled: agent?.reminders_enabled ?? false,
  reminders_timer_seconds: agent?.reminders_timer_seconds ?? 30,
  reminders_words: agent?.reminders_words ?? null,
  silence_hangup_enabled: agent?.silence_hangup_enabled ?? false,
  silence_hangup_seconds: agent?.silence_hangup_seconds ?? 30,
  silence_hangup_message: agent?.silence_hangup_message || '',
  max_call_duration_enabled: agent?.max_call_duration_enabled ?? false,
  max_call_duration_seconds: agent?.max_call_duration_seconds ?? 600,
  max_call_duration_message: agent?.max_call_duration_message || '',
  liveavatar_enabled: agent?.liveavatar_enabled ?? false,
  liveavatar_avatar_id: agent?.liveavatar_avatar_id || '',
  chat_reminder_enabled: agent?.chat_reminder_enabled ?? false,
  chat_reminder_seconds: agent?.chat_reminder_seconds ?? 30,
  chat_reminder_type: agent?.chat_reminder_type || 'static',
  chat_reminder_message: agent?.chat_reminder_message || '',
  chat_auto_close_enabled: agent?.chat_auto_close_enabled ?? false,
  chat_auto_close_seconds: agent?.chat_auto_close_seconds ?? 30,
  chat_auto_close_message: agent?.chat_auto_close_message || '',
  agent_end_call_enabled: agent?.agent_end_call_enabled ?? false,
  agent_end_call_speak_before: agent?.agent_end_call_speak_before ?? false,
  agent_end_call_message_type: agent?.agent_end_call_message_type || 'prompt',
  agent_end_call_message: agent?.agent_end_call_message || '',
  agent_end_call_goodbye_phrases: agent?.agent_end_call_goodbye_phrases || null,
  voicemail_drop_enabled: agent?.voicemail_drop_enabled ?? false,
  voicemail_message: agent?.voicemail_message || '',
  dtmf_enabled: agent?.dtmf_enabled ?? false,
  dtmf_ivr_detection: agent?.dtmf_ivr_detection ?? false,
  ivr_qa_mode: agent?.ivr_qa_mode ?? false,
  ivr_qa_max_turns: agent?.ivr_qa_max_turns ?? 25,
  ivr_qa_max_duration_s: agent?.ivr_qa_max_duration_s ?? 600,
  ivr_qa_goal: agent?.ivr_qa_goal || 'reach_operator',
  ivr_qa_prompt_override: agent?.ivr_qa_prompt_override || '',
  _end_call_prompt_draft: (agent?.agent_end_call_message_type || 'prompt') === 'prompt' ? (agent?.agent_end_call_message || '') : '',
  _end_call_static_draft: (agent?.agent_end_call_message_type || 'prompt') === 'static' ? (agent?.agent_end_call_message || '') : '',
  dtln_enabled: agent?.dtln_enabled ?? false,
  dtln_strength: agent?.dtln_strength ?? 0.5,
  ai_acoustics_enabled: agent?.ai_acoustics_enabled ?? false,
  ai_acoustics_enhancement_level: agent?.ai_acoustics_enhancement_level ?? 0.8,
  ai_acoustics_model: agent?.ai_acoustics_model ?? 'quail_vf_l',
});

const AgentManager = ({ showArchived = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [agentsList, setAgentsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [testingAgent, setTestingAgent] = useState(null);
  const [embeddingAgent, setEmbeddingAgent] = useState(null);
  const [chatEmbeddingAgent, setChatEmbeddingAgent] = useState(null);
  const [voiceWidgetAgent, setVoiceWidgetAgent] = useState(null);
  const [comboWidgetAgent, setComboWidgetAgent] = useState(null);
  const [comboWidgetV2Agent, setComboWidgetV2Agent] = useState(null);
  const [logoVoiceWidgetAgent, setLogoVoiceWidgetAgent] = useState(null);
  const [avatarPanelAgent, setAvatarPanelAgent] = useState(null);
  const [cobrowseAgent, setCobrowseAgent] = useState(null);
  const [dynamicVariablesAgent, setDynamicVariablesAgent] = useState(null);
  const [dynamicVariablesLiveAvatarAgent, setDynamicVariablesLiveAvatarAgent] = useState(null);
  const [toast, setToast] = useState(null);
  const [agentMetrics, setAgentMetrics] = useState({}); // Store metrics per agent
  const [formData, setFormData] = useState(() => createDefaultFormState());
  const [archiveModal, setArchiveModal] = useState({ open: false, agentId: null, agentName: '', action: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    loadAgents();
  }, [showArchived]);

  // Refresh metrics when agents list changes (but only if agents exist)
  useEffect(() => {
    if (agentsList.length > 0 && !loading) {
      loadAgentMetrics();
    }
  }, [agentsList.length]); // Only depend on length to avoid infinite loops

  // Auto-open agent from URL ?agentId= once the list has loaded
  useEffect(() => {
    if (loading || agentsList.length === 0) return;
    const agentId = searchParams.get('agentId');
    if (agentId) {
      const agent = agentsList.find(a => String(a.id) === String(agentId));
      if (agent) {
        setEditingAgent(agent);
        setFormData(mapAgentToFormState(agent));
        setShowForm(true);
      }
    }
  }, [loading, agentsList.length]);

  const loadAgents = async () => {
    try {
      const response = await agents.list(showArchived);
      // Sort agents alphabetically by name (A to Z)
      const sortedAgents = [...response.data].sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setAgentsList(sortedAgents);
    } catch (error) {
      console.error('Failed to load agents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort agents based on search query
  const filteredAndSortedAgents = useMemo(() => {
    let filtered = agentsList;
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = agentsList.filter(agent => 
        (agent.name || '').toLowerCase().includes(query) ||
        (agent.system_prompt || '').toLowerCase().includes(query)
      );
    }
    
    // Sort alphabetically (already sorted in loadAgents, but ensure it's maintained)
    return filtered.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [agentsList, searchQuery]);

  const loadAgentMetrics = async () => {
    try {
      // Fetch all sessions (we'll need to paginate or get all)
      // For now, fetch multiple pages to get all sessions
      const allSessions = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore && page <= 10) { // Limit to 10 pages to avoid infinite loops
        try {
          const response = await sessions.list(page, 100);
          const data = response.data;
          allSessions.push(...data.sessions);
          
          if (data.sessions.length < 100 || page * 100 >= data.total) {
            hasMore = false;
          } else {
            page++;
          }
        } catch (error) {
          console.error('Failed to load sessions page:', error);
          hasMore = false;
        }
      }

      // Calculate metrics per agent
      const metrics = {};
      
      agentsList.forEach(agent => {
        const agentSessions = allSessions.filter(s => s.agent_id === agent.id);
        const activeSessions = agentSessions.filter(s => s.status === 'active');
        
        metrics[agent.id] = {
          activeConversations: activeSessions.length,
          totalConversations: agentSessions.length,
        };
      });

      setAgentMetrics(metrics);
    } catch (error) {
      console.error('Failed to load agent metrics:', error);
    }
  };

  const handleSubmit = async (e, formDataOverride) => {
    e.preventDefault();
    try {
      // Use formDataOverride when provided (e.g. from AgentFormModal with flushed flow data)
      const dataToSave = formDataOverride || formData;
      const payload = { ...dataToSave };
      if (payload.voice_provider !== 'sonic-3') {
        payload.sonic_speed = null;
        payload.sonic_volume = null;
        payload.sonic_emotion = null;
      }
      if (payload.voice_provider !== 'fishaudio') {
        // Leave the stored voice/model alone so switching back keeps the setup,
        // but never send tuning values that belong to another provider.
        payload.fishaudio_speed = null;
        payload.fishaudio_volume = null;
        payload.fishaudio_temperature = null;
        payload.fishaudio_top_p = null;
        payload.fishaudio_chunk_length = null;
        payload.fishaudio_min_chunk_length = null;
        payload.fishaudio_max_new_tokens = null;
        payload.fishaudio_early_stop_threshold = null;
        payload.fishaudio_expressive_enabled = false;
      } else if (!payload.fishaudio_expressive_enabled) {
        payload.fishaudio_expressive_instructions = null;
      }
      // Ensure elevenlabs_speed and elevenlabs_model are included when using ElevenLabs, or set to null otherwise
      if (payload.voice_provider !== 'elevenlabs') {
        payload.elevenlabs_speed = null;
        payload.elevenlabs_model = null;
      } else {
        // Ensure elevenlabs_speed is always included (even if it's the default 1.0)
        if (payload.elevenlabs_speed === undefined || payload.elevenlabs_speed === null) {
          payload.elevenlabs_speed = 1.0;
        }
        // CRITICAL: Always include elevenlabs_model when using ElevenLabs - use formData value explicitly
        // This ensures the selected model is always sent, preventing it from being lost
        payload.elevenlabs_model = dataToSave.elevenlabs_model || payload.elevenlabs_model || 'eleven_turbo_v2_5';
      }

      // Ensure custom_pii_types is saved even if it's just disabled defaults
      if (payload.custom_pii_types && typeof payload.custom_pii_types === 'string') {
        try {
          const piiData = JSON.parse(payload.custom_pii_types);
          // If it has disabled defaults but no custom types, ensure it's still saved
          if (piiData._disabled_defaults && Object.keys(piiData).length === 1) {
          }
        } catch (e) {
          console.warn('⚠️ Invalid custom_pii_types format:', payload.custom_pii_types);
        }
      }

      // Ensure background_audio_enabled is always included (even if false)
      // This prevents it from being omitted when the value is false
      if (payload.background_audio_enabled === undefined) {
        payload.background_audio_enabled = false;
      }
      // Ensure reminders_enabled is always included (even if false)
      if (payload.reminders_enabled === undefined) {
        payload.reminders_enabled = false;
      }
      // Ensure agent_end_call_enabled is always included (even if false)
      if (payload.agent_end_call_enabled === undefined) {
        payload.agent_end_call_enabled = false;
      }
      // Ensure voicemail_drop_enabled is always included (even if false)
      if (payload.voicemail_drop_enabled === undefined) {
        payload.voicemail_drop_enabled = false;
      }
      // Ensure outbound_greeting_enabled is always included (even if false), and
      // send an empty outbound greeting as an empty string so clearing the box
      // actually clears the column instead of leaving the old message behind.
      if (payload.outbound_greeting_enabled === undefined) {
        payload.outbound_greeting_enabled = false;
      }
      if (payload.outbound_greeting_message === undefined || payload.outbound_greeting_message === null) {
        payload.outbound_greeting_message = '';
      }
      // Same for the outbound who-speaks-first override and the outbound
      // greeting's non-interruptible flag: send false explicitly so turning
      // either one off actually clears the column.
      if (payload.outbound_who_speaks_first_enabled === undefined) {
        payload.outbound_who_speaks_first_enabled = false;
      }
      if (!payload.outbound_who_speaks_first) {
        payload.outbound_who_speaks_first = 'ai';
      }
      if (payload.outbound_greeting_non_interruptible === undefined) {
        payload.outbound_greeting_non_interruptible = false;
      }
      // Call limits: a number box left empty must not fail the save
      if (payload.silence_hangup_seconds === '' || payload.silence_hangup_seconds == null || Number.isNaN(payload.silence_hangup_seconds)) {
        payload.silence_hangup_seconds = 30;
      }
      if (payload.max_call_duration_seconds === '' || payload.max_call_duration_seconds == null || Number.isNaN(payload.max_call_duration_seconds)) {
        payload.max_call_duration_seconds = 600;
      }
      // Ensure DTMF flags are always included (even if false)
      if (payload.dtmf_enabled === undefined) {
        payload.dtmf_enabled = false;
      }
      if (payload.dtmf_ivr_detection === undefined) {
        payload.dtmf_ivr_detection = false;
      }
      // Ensure IVR QA mode is always included (even if false), and keep the
      // sub-settings consistent with it: with the toggle off the caps and the
      // prompt override are meaningless, and an empty override must reach the
      // API as null so the column clears instead of storing an empty string.
      if (payload.ivr_qa_mode === undefined) {
        payload.ivr_qa_mode = false;
      }
      if (!payload.ivr_qa_mode) {
        payload.ivr_qa_prompt_override = null;
      } else if (typeof payload.ivr_qa_prompt_override === 'string' && !payload.ivr_qa_prompt_override.trim()) {
        payload.ivr_qa_prompt_override = null;
      }
      // Conditional transfer rules: drop half-filled rows; null clears the column
      payload.transfer_rules = serializeTransferRules(payload.transfer_rules);
      // Strip internal draft fields (not API fields)
      delete payload._end_call_prompt_draft;
      delete payload._end_call_static_draft;
      // Ensure agent_transfer_enabled is always included and is a proper boolean
      if (payload.agent_transfer_enabled === undefined || payload.agent_transfer_enabled === null) {
        payload.agent_transfer_enabled = false;
      } else {
        // Force boolean conversion
        payload.agent_transfer_enabled = Boolean(payload.agent_transfer_enabled);
      }

      // Outbound greeting trace. Logs the fields leaving the browser so a value
      // that comes back off can be blamed on the request or on the response,
      // not guessed at. Pair these with the OUTBOUND[...] lines in the API log.
      const outboundDebugKeys = ['outbound_greeting_enabled','outbound_greeting_message','outbound_who_speaks_first_enabled','outbound_who_speaks_first','outbound_greeting_non_interruptible'];
      console.log('🔎 OUTBOUND[0-payload leaving browser]', Object.fromEntries(
        outboundDebugKeys.map((k) => [k, k in payload ? payload[k] : '<absent>'])
      ));

      const response = editingAgent
        ? await agents.update(editingAgent.id, payload)
        : await agents.create(payload);

      await loadAgents();
      await loadAgentMetrics(); // Refresh metrics after agent update

      if (editingAgent) {
        // Reload the agent from the API to get the complete updated state
        // This ensures we have all fields including agent_transfer_enabled
        try {
          const reloadedAgent = await agents.get(editingAgent.id);
          const updatedAgent = reloadedAgent?.data || response?.data || editingAgent;
          console.log('🔎 OUTBOUND[6-GET response the form repopulates from]', Object.fromEntries(
            outboundDebugKeys.map((k) => [k, updatedAgent && k in updatedAgent ? updatedAgent[k] : '<absent>'])
          ));
          setEditingAgent(updatedAgent);
          // Ensure agent_transfer_enabled is properly set as boolean
          const mappedData = mapAgentToFormState(updatedAgent);
          mappedData.agent_transfer_enabled = Boolean(mappedData.agent_transfer_enabled ?? false);
          setFormData(mappedData);
        } catch (reloadError) {
          // Fallback to response data if reload fails
          const updatedAgent = response?.data || editingAgent;
          setEditingAgent(updatedAgent);
          const mappedData = mapAgentToFormState(updatedAgent);
          mappedData.agent_transfer_enabled = Boolean(mappedData.agent_transfer_enabled ?? false);
          setFormData(mappedData);
        }
      } else {
        resetForm();
      }

      setToast({ message: editingAgent ? 'Agent updated successfully!' : 'Agent created successfully!', type: 'success' });
    } catch (error) {
      console.error('Failed to save agent:', error);
      setToast({ 
        message: 'Failed to save agent: ' + (error.response?.data?.detail || error.message), 
        type: 'error' 
      });
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData(mapAgentToFormState(agent));
    setShowForm(true);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('agentId', agent.id);
      return next;
    }, { replace: true });
  };

  const handleArchive = async () => {
    try {
      await agents.archive(archiveModal.agentId);
      await loadAgents();
      await loadAgentMetrics(); // Refresh metrics after agent archiving
      setToast({ message: `${archiveModal.agentName} has been archived.`, type: 'success' });
      setArchiveModal({ open: false, agentId: null, agentName: '' });
    } catch (error) {
      console.error('Failed to archive agent:', error);
      setToast({ 
        message: 'Failed to archive agent: ' + (error.response?.data?.detail || error.message), 
        type: 'error' 
      });
    }
  };

  const handleUnarchive = async () => {
    try {
      await agents.unarchive(archiveModal.agentId);
      await loadAgents();
      await loadAgentMetrics(); // Refresh metrics after agent unarchiving
      setToast({ message: `${archiveModal.agentName} has been unarchived.`, type: 'success' });
      setArchiveModal({ open: false, agentId: null, agentName: '' });
    } catch (error) {
      console.error('Failed to unarchive agent:', error);
      setToast({ 
        message: 'Failed to unarchive agent: ' + (error.response?.data?.detail || error.message), 
        type: 'error' 
      });
    }
  };

  const handleDelete = async (agentId, agentName) => {
    if (window.confirm(`Are you sure you want to delete "${agentName}"?`)) {
      try {
        await agents.delete(agentId);
        await loadAgents();
        await loadAgentMetrics(); // Refresh metrics after agent deletion
        setToast({ message: `${agentName} has been removed from your agents.`, type: 'success' });
      } catch (error) {
        console.error('Failed to delete agent:', error);
        setToast({ 
          message: 'Failed to delete agent: ' + (error.response?.data?.detail || error.message), 
          type: 'error' 
        });
      }
    }
  };

  const resetForm = () => {
    setFormData(createDefaultFormState());
    setEditingAgent(null);
    setShowForm(false);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('agentId');
      return next;
    }, { replace: true });
  };

  const getAgentStatus = (agent) => {
    if (agent.is_active) {
      return 'active';
    }
    // You can add more logic here to determine idle vs offline
    return 'offline';
  };

  const getStatusClasses = (status) => {
    switch (status) {
      case 'active':
        return 'bg-primary text-primary-foreground';
      case 'idle':
        return 'bg-warning text-warning-foreground';
      case 'offline':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <Phone className="w-3 h-3" />;
      case 'idle':
        return <Mic className="w-3 h-3" />;
      case 'offline':
        return <PhoneOff className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const handleCopyAgentId = (agentId) => {
    navigator.clipboard.writeText(agentId);
    setToast({ message: 'Agent ID copied to clipboard', type: 'success' });
  };

  const handleConfigureAgent = (agent) => {
    handleEdit(agent);
  };

  const handleToggleAgent = async (agent) => {
    try {
      await agents.update(agent.id, { is_active: !agent.is_active });
      await loadAgents();
      await loadAgentMetrics(); // Refresh metrics after toggle
      setToast({ 
        message: `Agent ${!agent.is_active ? 'activated' : 'deactivated'} successfully`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Failed to toggle agent:', error);
      setToast({ 
        message: 'Failed to toggle agent status', 
        type: 'error' 
      });
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading agents..." />
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="w-full flex flex-col" style={{ minHeight: 'calc(100vh - 100px)' }}>
        <AgentFormModal
          editingAgent={editingAgent}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={resetForm}
        />
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{showArchived ? 'Archived Agents' : 'Voice Agents'}</h2>
            <p className="text-sm text-muted-foreground">{showArchived ? 'View and restore archived agents' : 'Manage and monitor your AI voice agents'}</p>
          </div>
          {!showArchived && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2"
            >
              <Bot className="w-4 h-4" />
              Create New Agent
            </button>
          )}
        </div>

        {/* Search Bar and View Toggle */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search agents by name or system prompt..."
              className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-lg border border-border">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              title="Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              title="List View"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Agent Cards Grid or List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
          {filteredAndSortedAgents.map((agent, index) => {
            const status = getAgentStatus(agent);
            const metrics = agentMetrics[agent.id] || { activeConversations: 0, totalConversations: 0 };
            
            return (
            <div
              key={agent.id}
              className="group relative overflow-hidden border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:border-primary/40 hover:bg-card animate-slide-in-up"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Card Header */}
              <div className="p-3 pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    {/* Agent Icon */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                      <Bot className="w-5 h-5 text-primary-foreground" />
                </div>
                
                    {/* Agent Name & Status */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm truncate text-foreground">{agent.name}</h3>
                        {agent.agent_type === 'flow' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-600 border border-blue-500/30">
                            <GitBranch className="w-3 h-3" />
                            Flow
                          </span>
                        )}
                      </div>
                      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getStatusClasses(status)}`}>
                        {getStatusIcon(status)}
                        <span className="capitalize">{status}</span>
                      </div>
                </div>
                </div>
                
                  {/* More Options Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-muted rounded-lg flex items-center justify-center">
                        <MoreVertical className="w-3.5 h-3.5 text-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!showArchived && (
                        <>
                          <DropdownMenuItem onClick={() => handleConfigureAgent(agent)}>
                            <Edit3 className="w-4 h-4 mr-2" />
                            Configure Agent
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEmbeddingAgent(agent)}>
                            <Code className="w-4 h-4 mr-2" />
                            Embed Agent (Voice)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setVoiceWidgetAgent(agent)}>
                            <Phone className="w-4 h-4 mr-2" />
                            Voice Call Widget
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setChatEmbeddingAgent(agent)}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Embed Chat
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setComboWidgetAgent(agent)}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Combo Widget (Chat + Voice)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setComboWidgetV2Agent(agent)}>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Combo Widget V2 (New Design)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setLogoVoiceWidgetAgent(agent)}>
                            <Circle className="w-4 h-4 mr-2" />
                            Logo Voice Widget
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setCobrowseAgent(agent)}>
                            <MousePointerClick className="w-4 h-4 mr-2" />
                            Co-browse Widget
                          </DropdownMenuItem>
                          {agent.liveavatar_enabled && (
                            <DropdownMenuItem onClick={() => setAvatarPanelAgent(agent)}>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Interactive Avatar Panel
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setDynamicVariablesAgent(agent)}>
                            <Code className="w-4 h-4 mr-2" />
                            Embed with Variables
                          </DropdownMenuItem>
                          {agent.liveavatar_enabled && (
                            <DropdownMenuItem onClick={() => setDynamicVariablesLiveAvatarAgent(agent)}>
                              <Code className="w-4 h-4 mr-2" />
                              Embed Live Avatar with Variables
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                      <DropdownMenuItem onClick={() => handleCopyAgentId(agent.id)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Agent ID
                      </DropdownMenuItem>
                      {!showArchived && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setArchiveModal({ open: true, agentId: agent.id, agentName: agent.name, action: 'archive' })}
                          >
                            <Archive className="w-4 h-4 mr-2" />
                            Archive Agent
                          </DropdownMenuItem>
                        </>
                      )}
                      {showArchived && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setArchiveModal({ open: true, agentId: agent.id, agentName: agent.name, action: 'unarchive' })}
                          >
                            <ArchiveRestore className="w-4 h-4 mr-2" />
                            Unarchive Agent
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              {/* Card Content */}
              <div className="px-3 pb-3 space-y-3">
                {/* Voice Activity */}
                <div className="relative bg-muted/20 rounded-lg p-3 border border-border/30">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase">Voice Activity</span>
                    {status === 'active' && (
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                        <span className="text-xs text-primary font-medium">LIVE</span>
                </div>
              )}
                  </div>
                  <VoiceWaveform isActive={status === 'active'} height={24} barCount={20} />
                </div>

                {/* Performance Metrics */}
                <div className="grid grid-cols-2 gap-2.5 text-xs">
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Active Calls</div>
                    <div className="font-bold text-lg text-primary">{metrics.activeConversations}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Calls</div>
                    <div className="font-bold text-lg text-foreground">{metrics.totalConversations.toLocaleString()}</div>
                  </div>
                  <div className="space-y-1 col-span-2">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">AI Model</div>
                    <div className="font-semibold text-sm truncate text-foreground">{agent.llm_model}</div>
                  </div>
                </div>
                
                {/* Quick Actions */}
                <div className="flex gap-2 pt-2 border-t border-border/30">
                  <button
                    onClick={() => handleConfigureAgent(agent)}
                    disabled={showArchived}
                    className={`flex-1 h-9 text-xs font-medium px-3 py-2 bg-background border border-border rounded-lg transition-colors flex items-center justify-center gap-1 text-foreground ${
                      showArchived ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'
                    }`}
                  >
                    <Settings className="w-3 h-3" />
                    Configure
                  </button>
                  <button
                    onClick={() => setTestingAgent(agent)}
                    disabled={showArchived}
                    className={`flex-1 h-9 text-xs font-medium px-3 py-2 bg-primary text-primary-foreground rounded-lg transition-colors flex items-center justify-center gap-1 ${
                      showArchived ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/90'
                    }`}
                  >
                    <PhoneCall className="w-3 h-3" />
                    Test
                  </button>
                  <button
                    onClick={() => handleToggleAgent(agent)}
                    disabled={showArchived}
                    className={`flex-1 h-9 text-xs font-medium px-3 py-2 rounded-lg transition-colors flex items-center justify-center gap-1 ${
                      showArchived 
                        ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground'
                        : status === 'active'
                          ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                          : 'bg-success hover:bg-success/90 text-success-foreground'
                    }`}
                  >
                    {status === 'active' ? (
                      <>
                        <Pause className="w-3 h-3" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Play className="w-3 h-3" />
                        Start
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        
        {filteredAndSortedAgents.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            {searchQuery 
              ? `No agents found matching "${searchQuery}". Try a different search term.`
              : 'No agents found. Create your first agent to get started.'}
          </div>
        )}
      </div>
      ) : (
        /* List View */
        <div className="space-y-2" style={{ overflow: 'visible' }}>
          {filteredAndSortedAgents.map((agent, index) => {
            const status = getAgentStatus(agent);
            const metrics = agentMetrics[agent.id] || { activeConversations: 0, totalConversations: 0 };
            
            return (
              <div
                key={agent.id}
                className="group border border-border/50 bg-card/80 backdrop-blur-sm rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 hover:border-primary/40 hover:bg-card relative"
                style={{ overflow: 'visible' }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Left Section: Icon, Name, Status */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                        <Bot className="w-6 h-6 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-base text-foreground truncate">{agent.name}</h3>
                          {agent.agent_type === 'flow' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-600 border border-blue-500/30">
                              <GitBranch className="w-3 h-3" />
                              Flow
                            </span>
                          )}
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getStatusClasses(status)}`}>
                            {getStatusIcon(status)}
                            <span className="capitalize">{status}</span>
                          </div>
                          {status === 'active' && (
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                              <span className="text-xs text-primary font-medium">LIVE</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="font-medium">Model:</span>
                          <span>{agent.llm_model}</span>
                          <span className="font-medium">Voice:</span>
                          <span className="capitalize">{agent.voice_provider}</span>
                        </div>
                      </div>
                    </div>

                    {/* Middle Section: Metrics */}
                    <div className="flex items-center gap-6 flex-shrink-0">
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Active</div>
                        <div className="font-bold text-lg text-primary">{metrics.activeConversations}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total</div>
                        <div className="font-bold text-lg text-foreground">{metrics.totalConversations.toLocaleString()}</div>
                      </div>
                      <div className="w-24">
                        <VoiceWaveform isActive={status === 'active'} height={20} barCount={15} />
                      </div>
                    </div>

                    {/* Right Section: Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
                      <button
                        onClick={() => handleConfigureAgent(agent)}
                        disabled={showArchived}
                        className={`h-9 px-3 text-xs font-medium bg-background border border-border rounded-lg transition-colors flex items-center gap-1 text-foreground ${
                          showArchived ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'
                        }`}
                      >
                        <Settings className="w-3 h-3" />
                        Configure
                      </button>
                      <button
                        onClick={() => setTestingAgent(agent)}
                        disabled={showArchived}
                        className={`h-9 px-3 text-xs font-medium bg-primary text-primary-foreground rounded-lg transition-colors flex items-center gap-1 ${
                          showArchived ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/90'
                        }`}
                      >
                        <PhoneCall className="w-3 h-3" />
                        Test
                      </button>
                      <button
                        onClick={() => handleToggleAgent(agent)}
                        disabled={showArchived}
                        className={`h-9 px-3 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 ${
                          showArchived 
                            ? 'opacity-50 cursor-not-allowed bg-muted text-muted-foreground'
                            : status === 'active'
                              ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground'
                              : 'bg-success hover:bg-success/90 text-success-foreground'
                        }`}
                      >
                        {status === 'active' ? (
                          <>
                            <Pause className="w-3 h-3" />
                            Stop
                          </>
                        ) : (
                          <>
                            <Play className="w-3 h-3" />
                            Start
                          </>
                        )}
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="h-9 w-9 p-0 hover:bg-muted rounded-lg flex items-center justify-center transition-colors relative">
                            <MoreVertical className="w-4 h-4 text-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!showArchived && (
                            <>
                              <DropdownMenuItem onClick={() => handleConfigureAgent(agent)}>
                                <Edit3 className="w-4 h-4 mr-2" />
                                Configure Agent
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setEmbeddingAgent(agent)}>
                                <Code className="w-4 h-4 mr-2" />
                                Embed Agent (Voice)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setVoiceWidgetAgent(agent)}>
                                <Phone className="w-4 h-4 mr-2" />
                                Voice Call Widget
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setChatEmbeddingAgent(agent)}>
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Embed Chat
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setComboWidgetAgent(agent)}>
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Combo Widget (Chat + Voice)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setComboWidgetV2Agent(agent)}>
                                <Sparkles className="w-4 h-4 mr-2" />
                                Combo Widget V2 (New Design)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setLogoVoiceWidgetAgent(agent)}>
                                <Circle className="w-4 h-4 mr-2" />
                                Logo Voice Widget
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setCobrowseAgent(agent)}>
                                <MousePointerClick className="w-4 h-4 mr-2" />
                                Co-browse Widget
                              </DropdownMenuItem>
                              {agent.liveavatar_enabled && (
                                <DropdownMenuItem onClick={() => setAvatarPanelAgent(agent)}>
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  Interactive Avatar Panel
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => setDynamicVariablesAgent(agent)}>
                                <Code className="w-4 h-4 mr-2" />
                                Embed with Variables
                              </DropdownMenuItem>
                              {agent.liveavatar_enabled && (
                                <DropdownMenuItem onClick={() => setDynamicVariablesLiveAvatarAgent(agent)}>
                                  <Code className="w-4 h-4 mr-2" />
                                  Embed Live Avatar with Variables
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                          <DropdownMenuItem onClick={() => handleCopyAgentId(agent.id)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Agent ID
                          </DropdownMenuItem>
                          {!showArchived && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setArchiveModal({ open: true, agentId: agent.id, agentName: agent.name, action: 'archive' })}
                              >
                                <Archive className="w-4 h-4 mr-2" />
                                Archive Agent
                              </DropdownMenuItem>
                            </>
                          )}
                          {showArchived && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setArchiveModal({ open: true, agentId: agent.id, agentName: agent.name, action: 'unarchive' })}
                              >
                                <ArchiveRestore className="w-4 h-4 mr-2" />
                                Unarchive Agent
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {filteredAndSortedAgents.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              {searchQuery 
                ? `No agents found matching "${searchQuery}". Try a different search term.`
                : 'No agents found. Create your first agent to get started.'}
            </div>
          )}
        </div>
      )}

      {/* Test Agent Interface Modal */}
      {testingAgent && (
        <TestAgentInterface
          agent={testingAgent}
          onClose={() => setTestingAgent(null)}
        />
      )}

      {/* Embed Modal */}
      {embeddingAgent && (
        <EmbedModal
          agent={embeddingAgent}
          onClose={() => setEmbeddingAgent(null)}
        />
      )}

      {/* Chat Embed Modal */}
      {chatEmbeddingAgent && (
        <ChatEmbedModal
          agent={chatEmbeddingAgent}
          onClose={() => setChatEmbeddingAgent(null)}
        />
      )}

      {/* Voice Call Widget Embed Modal */}
      {voiceWidgetAgent && (
        <VoiceWidgetEmbedModal
          agent={voiceWidgetAgent}
          onClose={() => setVoiceWidgetAgent(null)}
        />
      )}

      {/* Combo Widget (Chat + Voice) Embed Modal */}
      {comboWidgetAgent && (
        <UnifiedWidgetEmbedModal
          agent={comboWidgetAgent}
          onClose={() => setComboWidgetAgent(null)}
        />
      )}

      {/* Combo Widget V2 (Flagship Blue / Dark G design) Embed Modal */}
      {comboWidgetV2Agent && (
        <ComboWidgetV2EmbedModal
          agent={comboWidgetV2Agent}
          onClose={() => setComboWidgetV2Agent(null)}
        />
      )}

      {logoVoiceWidgetAgent && (
        <LogoVoiceWidgetEmbedModal
          agent={logoVoiceWidgetAgent}
          onClose={() => setLogoVoiceWidgetAgent(null)}
        />
      )}

      {/* Co-browse Widget (voice agent that can guide the host page) */}
      {cobrowseAgent && (
        <CobrowseEmbedModal
          agent={cobrowseAgent}
          onClose={() => setCobrowseAgent(null)}
          onSaved={(updated) => setAgentsList((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)))}
        />
      )}

      {/* Interactive Avatar Panel (live avatar drawer / spotlight) */}
      {avatarPanelAgent && (
        <AvatarPanelEmbedModal
          agent={avatarPanelAgent}
          onClose={() => setAvatarPanelAgent(null)}
        />
      )}

      {dynamicVariablesAgent && (
        <EmbedDynamicVariables
          agent={dynamicVariablesAgent}
          onClose={() => setDynamicVariablesAgent(null)}
        />
      )}

      {dynamicVariablesLiveAvatarAgent && (
        <EmbedDynamicVariablesLiveAvatar
          agent={dynamicVariablesLiveAvatarAgent}
          onClose={() => setDynamicVariablesLiveAvatarAgent(null)}
        />
      )}

      {/* Archive/Unarchive Confirmation Modal */}
      {archiveModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 w-full max-w-md mx-4 shadow-elegant">
            <div className="flex items-center gap-3 mb-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                archiveModal.action === 'archive' 
                  ? 'bg-warning/20' 
                  : 'bg-primary/20'
              }`}>
                {archiveModal.action === 'archive' ? (
                  <Archive className="w-5 h-5 text-warning" />
                ) : (
                  <ArchiveRestore className="w-5 h-5 text-primary" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                {archiveModal.action === 'archive' ? 'Archive Agent?' : 'Unarchive Agent?'}
              </h3>
            </div>
            
            <p className="text-muted-foreground text-sm mb-2">
              {archiveModal.action === 'archive' ? (
                <>
                  Are you sure you want to archive <span className="text-foreground font-medium">"{archiveModal.agentName}"</span>?
                </>
              ) : (
                <>
                  Are you sure you want to unarchive <span className="text-foreground font-medium">"{archiveModal.agentName}"</span>?
                </>
              )}
            </p>
            <p className="text-muted-foreground text-xs mb-6">
              {archiveModal.action === 'archive' 
                ? 'The agent will be moved to the archived agents tab and cannot be used until unarchived.'
                : 'The agent will be restored and can be used again.'}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setArchiveModal({ open: false, agentId: null, agentName: '', action: null })}
                className="px-4 py-2 bg-muted/50 hover:bg-muted border border-border/60 text-foreground rounded-lg transition-all duration-300 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={archiveModal.action === 'archive' ? handleArchive : handleUnarchive}
                className={`px-4 py-2 rounded-lg transition-all duration-300 text-sm font-medium ${
                  archiveModal.action === 'archive'
                    ? 'bg-warning hover:bg-warning/90 text-warning-foreground'
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                }`}
              >
                {archiveModal.action === 'archive' ? 'Archive' : 'Unarchive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AgentManager;
